import {
	UpdateSecurityFormDto,
	UpdateSecurityShape,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/security.zod.ts';
import { updateSecurity } from '@dealstack/frontend/shared/dataExchange/services/securities.ts';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { DataSecurityTct } from '@dealstack/shared/lib/generated';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

export enum EDIT_SECURITY_FORM_FIELD {
	name = 'name',
	type = 'type',
	currency = 'currency',
}

interface Args {
	security?: DataSecurityTct;
	opened: boolean;
	onSaved?: () => void;
	onClose?: () => void;
}

const toFormValues = (security?: DataSecurityTct) => ({
	id: security?.id,
	currency: security?.currency,
	name: security?.name,
	personId: security?.issuerId,
	type: security?.type,
});

export const useEditSecurityDrawerController = ({ security, opened, onSaved, onClose }: Args) => {
	const tCapManage = useTranslationCapManage();

	const updateSecurityRequest = useMutation({
		mutationFn: updateSecurity,
	});

	const editSecurityForm = useForm<UpdateSecurityFormDto>({
		mode: 'all',
		resolver: zodResolver(UpdateSecurityShape),
		defaultValues: toFormValues(security),
		shouldFocusError: false,
	});

	// Keyed on `opened` as well as the security: a caller that keeps the security selected after
	// closing would otherwise reopen onto the abandoned edits, and Save would submit them.
	useEffect(() => {
		if (opened && security) {
			editSecurityForm.reset(toFormValues(security));
		}
	}, [opened, security]);

	const onSubmit = (formValues: UpdateSecurityFormDto) => {
		updateSecurityRequest.mutate(
			// No securityProgrammeSettings: this drawer cannot edit vesting, and sending the value
			// it held when the drawer opened would write back over a toggle made since.
			{
				id: formValues.id,
				name: formValues.name,
				type: formValues.type,
			},
			{
				onSuccess: () => {
					onSaved?.();
					showToastV2({
						title: tCapManage('toasts.success.security_updated_successfully'),
					});
					onClose?.();
				},
			}
		);
	};

	return {
		updateSecurityRequest,
		onSubmit,
		editSecurityForm,
	};
};
